import axios from "./helpers/axios";
import { Contact } from "./types/contact";

export const getContactById = (contactId: string): Promise<Contact> => {
  return new Promise((resolve, reject) => {
    axios
      .get(
        `${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.contact.get?ID=${contactId}`
      )
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};

export const getContactsByIds = (
  contactIds: string[],
  accountId: string
): Promise<Contact[]> => {
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.contact.list`, {
        FILTER: {
          ID: contactIds,
          UF_CRM_PARNERUS_ACCOUNT_ID: accountId,
        },
      })
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};

export const getContactsByNameAndLastname = (
  textFilter: string,
  accountId: string
): Promise<Contact[]> => {
  return new Promise((resolve, reject) => {
    axios
      .get(
        `${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.contact.list?FILTER[%NAME%]=${textFilter}&FILTER[%LAST_NAME%]=${textFilter}&FILTER[UF_CRM_PARNERUS_ACCOUNT_ID]=${accountId}&ORDER[ID]=DESC`
      )
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};

/**
 * Busca un contacto ya existente en Bitrix por email o telefono, para no duplicar.
 *
 * Se usa crm.duplicate.findbycomm, que es el buscador de duplicados de Bitrix y
 * normaliza los telefonos. Solo se reutiliza si hay UN unico candidato y no
 * pertenece ya a OTRA cuenta de Parnerus.
 */
export const findExistingContact = async (
  email: string | undefined,
  phone: string | undefined,
  accountId: string
): Promise<string | null> => {
  const buscar = async (type: "EMAIL" | "PHONE", value?: string) => {
    if (!value?.trim()) return [];
    const response = await axios.post(
      `${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.duplicate.findbycomm`,
      { entity_type: "CONTACT", type, values: [value.trim()] }
    );
    return (response.data.result?.CONTACT ?? []) as string[];
  };

  try {
    const encontrados = [...(await buscar("EMAIL", email)), ...(await buscar("PHONE", phone))];
    const ids = encontrados.filter((id, i) => encontrados.indexOf(id) === i);
    if (!ids.length) return null;

    const detalle = await axios.post(
      `${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.contact.list`,
      { FILTER: { ID: ids }, SELECT: ["ID", "UF_CRM_PARNERUS_ACCOUNT_ID"] }
    );
    const matches: { ID: string; UF_CRM_PARNERUS_ACCOUNT_ID?: string }[] =
      detalle.data.result ?? [];
    // Mismo criterio que en empresas: primero el de esta cuenta, para no crear un
    // tercer registro donde ya hay un duplicado.
    const propios = matches.filter(
      (m) => m.UF_CRM_PARNERUS_ACCOUNT_ID === accountId
    );
    if (propios.length === 1) return propios[0].ID;
    const libres = matches.filter((m) => !m.UF_CRM_PARNERUS_ACCOUNT_ID);
    if (!propios.length && libres.length === 1) return libres[0].ID;
    return null;
  } catch {
    return null;
  }
};

export const createContact = (
  contact: Contact,
  companyId: string | undefined
): Promise<string> => {
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.contact.add`, {
        FIELDS: {
          ...contact,
          COMPANY_ID: companyId,
        },
      })
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};

/**
 * Pone al Mediador de la cuenta como First Contact de un contacto.
 *
 * No se puede mandar en el alta: una automatizacion de la cuenta que corre al
 * crear el contacto deja First Contact vacio. Corre durante el alta, asi que un
 * update inmediatamente despues queda firme (verificado a 2, 10, 30 y 60 segundos).
 */
export const setContactFirstContact = (
  contactId: string,
  mediadorId: string | number
): Promise<string> => {
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.contact.update`, {
        ID: contactId,
        FIELDS: {
          UF_CRM_1759936128: mediadorId,
        },
      })
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};

export const updateContact = (contact: Partial<Contact>): Promise<string> => {
  return new Promise((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_BITRIX_WEBHOOK}/crm.contact.update`, {
        ID: contact.ID,
        FIELDS: {
          ...contact,
        },
      })
      .then((response) => resolve(response.data.result))
      .catch((error) => reject(error.message));
  });
};
